import React, { useState } from 'react';
import { 
  BookOpen, 
  FileText, 
  Download, 
  Filter, 
  Search, 
  ChevronRight,
  Globe,
  Calendar,
  Users,
  ArrowRight,
  Check,
  AlertTriangle,
  Loader,
  RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeader from '../components/shared/SectionHeader';
import { Card, Badge } from '../components/ui';

// Sample publications data
const publicationsData = [
  {
    id: 'avnopenai2025',
    title: 'AVN Network - OpenAI Integration Proposal',
    authors: 'AVN Research Team',
    date: '2025-04-01',
    abstract: 'A comprehensive proposal for integrating OpenAI\'s capabilities to enhance AVN\'s existing AI features and create more powerful, context-aware agricultural solutions.',
    downloadUrl: '/docs/avn-openai-proposal.md',
    thumbnailUrl: 'https://images.unsplash.com/photo-1593113598332-cd288d649433',
    category: 'proposal',
    tags: ['AI', 'OpenAI', 'Integration', 'Agriculture'],
    featured: true
  },
  {
    id: 'regenerative2024',
    title: 'Regenerative Agriculture in East Africa: AVN Case Studies',
    authors: 'Dr. Sarah Kimani, Edward Hamlet, Prof. James Ochieng',
    date: '2024-11-15',
    abstract: 'This paper presents five case studies from East Africa showcasing successful implementation of regenerative agricultural practices through the AVN network, with measurable improvements in soil health, yields, and farmer income.',
    downloadUrl: '#',
    thumbnailUrl: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399',
    category: 'case-study',
    tags: ['Regenerative Agriculture', 'East Africa', 'Soil Health', 'Case Study'],
    featured: true
  },
  {
    id: 'hybidfarm2024',
    title: 'The Hybrid Farm Model: Merging Traditional Knowledge with Modern Technology',
    authors: 'Hamza Mbareche, Lisa Ndungu, Thomas Chen',
    date: '2024-08-22',
    abstract: 'An exploration of AVN\'s hybrid farming approach that combines indigenous agricultural wisdom with cutting-edge technology to create resilient, productive, and sustainable farming systems.',
    downloadUrl: '#',
    thumbnailUrl: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449',
    category: 'whitepaper',
    tags: ['Hybrid Farming', 'Indigenous Knowledge', 'Technology', 'Sustainable Agriculture'],
    featured: false
  },
  {
    id: 'supplychain2024',
    title: 'Blockchain-Verified Ethical Supply Chains: The AVN Approach',
    authors: 'Michael Chen, Dr. Emily Rodriguez',
    date: '2024-06-10',
    abstract: 'A technical paper detailing AVN\'s blockchain-based supply chain verification system, which ensures transparency, fair compensation, and ethical practices throughout the agricultural value chain.',
    downloadUrl: '#',
    thumbnailUrl: 'https://images.unsplash.com/photo-1561736778-92e52a7769ef',
    category: 'technical-paper',
    tags: ['Blockchain', 'Supply Chain', 'Transparency', 'Ethics'],
    featured: false
  },
  {
    id: 'coffeevalue2024',
    title: 'Increasing Value Capture for Coffee Farmers: The Unity Bean Initiative',
    authors: 'Edward Hamlet, Maria Santos',
    date: '2024-05-03',
    abstract: 'This report documents how the Unity Bean initiative has increased coffee farmers\' share of end-value from 6% to 50%, through innovative processing, direct market access, and value-added product development.',
    downloadUrl: '#',
    thumbnailUrl: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2285',
    category: 'report',
    tags: ['Coffee', 'Value Chain', 'Farmer Income', 'Unity Bean'],
    featured: true
  },
  {
    id: 'circularwaste2023',
    title: 'Circular Waste Management in Agricultural Processing: BSF Case Study',
    authors: 'Dr. Johnson Okello, Patricia Wekesa',
    date: '2023-12-18',
    abstract: 'An examination of how Black Soldier Fly larvae are being used to convert agricultural waste into high-protein animal feed and organic fertilizer in Uganda, creating a zero-waste circular economy.',
    downloadUrl: '#',
    thumbnailUrl: 'https://images.unsplash.com/photo-1563203369-26f2e4a5ccf7',
    category: 'case-study',
    tags: ['Circular Economy', 'Waste Management', 'Black Soldier Fly', 'Uganda'],
    featured: false
  },
];

// Publication categories
const categories = [
  { id: 'all', label: 'All Publications' },
  { id: 'whitepaper', label: 'Whitepapers' },
  { id: 'case-study', label: 'Case Studies' },
  { id: 'technical-paper', label: 'Technical Papers' },
  { id: 'report', label: 'Reports' },
  { id: 'proposal', label: 'Proposals' }
];

const PublicationsPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadFormVisible, setDownloadFormVisible] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: ''
  });
  const [downloading, setDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Filter publications based on active category and search term
  const filteredPublications = publicationsData.filter(pub => {
    const matchesCategory = activeCategory === 'all' || pub.category === activeCategory;
    const matchesSearch = 
      searchTerm === '' || 
      pub.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.abstract.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  // Featured publications
  const featuredPublications = publicationsData.filter(pub => pub.featured);

  const handleDownloadRequest = (publication: any) => {
    setSelectedPublication(publication);
    setDownloadFormVisible(true);
    // Reset form and states
    setFormData({ name: '', email: '', company: '', role: '' });
    setDownloadSuccess(false);
    setDownloadError('');
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      setDownloadError('Please provide your name and email');
      return;
    }
    
    setDownloading(true);
    setDownloadError('');
    
    try {
      // Log the download in the database
      const { error } = await supabase
        .from('whitepaper_downloads')
        .insert({
          name: formData.name,
          email: formData.email,
          company: formData.company || 'Not provided',
          role: formData.role || 'Not provided',
          downloaded_at: new Date().toISOString()
        });
        
      if (error) throw error;
      
      // Show success message
      setDownloadSuccess(true);
      
      // Close the form after a delay
      setTimeout(() => {
        setDownloadFormVisible(false);
        // Trigger the actual download
        if (selectedPublication.downloadUrl !== '#') {
          window.open(selectedPublication.downloadUrl, '_blank');
        }
      }, 2000);
      
    } catch (error: any) {
      console.error('Download error:', error);
      setDownloadError('Failed to process your download. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="relative bg-avn-purple-900 text-white">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 py-20 relative z-10">
          <AnimatedSection>
            <div className="max-w-4xl">
              <Badge 
                variant="purple" 
                className="inline-flex mb-6 bg-white/20 backdrop-blur-sm px-4 py-2 text-white"
                icon={<BookOpen className="h-5 w-5" />}
              >
                AVN Research & Publications
              </Badge>
              <h1 className="text-5xl font-bold mb-6">
                Agricultural Knowledge & Insights
              </h1>
              <p className="text-xl opacity-90 mb-8 leading-relaxed">
                Explore our research, case studies, and thought leadership on regenerative agriculture, ethical supply chains, and sustainable food systems.
              </p>
              <div className="max-w-2xl">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search publications..."
                    className="w-full pl-12 pr-4 py-3 rounded-full shadow-lg focus:ring-2 focus:ring-avn-purple-400 focus:outline-none"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Category Filters */}
        <div className="mb-12 flex flex-wrap items-center justify-center gap-3">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-2 rounded-full transition-colors ${
                activeCategory === category.id
                  ? 'bg-avn-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Featured Publications */}
        {activeCategory === 'all' && searchTerm === '' && (
          <div className="mb-16">
            <SectionHeader
              title="Featured Publications"
              subtitle="Our latest research and insights on sustainable agriculture and ethical food systems"
              icon={<FileText className="h-5 w-5" />}
              badgeText="Featured"
              badgeColor="purple"
            />

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              {featuredPublications.map((publication) => (
                <AnimatedSection key={publication.id} delay={0.1}>
                  <motion.div 
                    className="bg-white rounded-xl shadow-lg overflow-hidden h-full flex flex-col"
                    whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="h-48 relative">
                      <img 
                        src={publication.thumbnailUrl} 
                        alt={publication.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <Badge 
                          variant={publication.category === 'case-study' ? 'green' : 
                                  publication.category === 'technical-paper' ? 'blue' : 
                                  publication.category === 'whitepaper' ? 'amber' : 'purple'}
                          className="capitalize"
                        >
                          {publication.category.replace('-', ' ')}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex-1 p-6">
                      <h3 className="text-xl font-bold mb-3 line-clamp-2">{publication.title}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(publication.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      </div>
                      <p className="text-gray-600 mb-4 line-clamp-3">{publication.abstract}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {publication.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                        {publication.tags.length > 3 && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                            +{publication.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-6 pt-0 mt-auto">
                      <button
                        onClick={() => handleDownloadRequest(publication)}
                        className="w-full bg-avn-purple-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 hover:bg-avn-purple-700 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        <span>Download Publication</span>
                      </button>
                    </div>
                  </motion.div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        )}

        {/* All Publications */}
        <div>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-800">
              {activeCategory === 'all' ? 'All Publications' : 
               `${categories.find(c => c.id === activeCategory)?.label}`}
            </h2>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <RefreshCw className="h-4 w-4" />
              <span>{filteredPublications.length} results</span>
            </div>
          </div>

          {filteredPublications.length === 0 ? (
            <div className="bg-white p-8 rounded-xl shadow text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-700 mb-2">No publications found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your search or filter criteria</p>
              <button 
                onClick={() => {setActiveCategory('all'); setSearchTerm('');}}
                className="text-avn-purple-600 font-medium hover:text-avn-purple-700 flex items-center gap-1 mx-auto"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Reset filters</span>
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredPublications.map((publication) => (
                <AnimatedSection key={publication.id}>
                  <Card className="flex flex-col md:flex-row overflow-hidden">
                    <div className="w-full md:w-48 h-48 md:h-auto flex-shrink-0">
                      <img 
                        src={publication.thumbnailUrl} 
                        alt={publication.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 p-6">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <h3 className="text-xl font-bold mb-2">{publication.title}</h3>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              <span>{publication.authors}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              <span>{new Date(publication.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            </div>
                            <Badge 
                              variant={publication.category === 'case-study' ? 'green' : 
                                      publication.category === 'technical-paper' ? 'blue' : 
                                      publication.category === 'whitepaper' ? 'amber' : 'purple'}
                              className="capitalize"
                            >
                              {publication.category.replace('-', ' ')}
                            </Badge>
                          </div>
                          <p className="text-gray-600 mb-4">{publication.abstract}</p>
                          <div className="flex flex-wrap gap-2">
                            {publication.tags.map((tag, index) => (
                              <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadRequest(publication)}
                          className="bg-avn-purple-600 text-white py-2 px-4 rounded-lg flex items-center gap-2 hover:bg-avn-purple-700 transition-colors"
                        >
                          <Download className="h-4 w-4" />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>

        {/* Submit Research CTA */}
        <div className="mt-16">
          <Card className="bg-gradient-to-r from-avn-purple-900 to-avn-purple-800 text-white p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-2">Share Your Research</h3>
                <p className="opacity-90 mb-4">
                  Are you conducting research in sustainable agriculture, ethical supply chains, 
                  or related fields? Submit your papers for consideration in our publications library.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="bg-white text-avn-purple-900 py-3 px-6 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-100 transition-colors">
                    <FileText className="h-5 w-5" />
                    <span>Submit Research</span>
                  </button>
                  <button className="bg-transparent text-white border border-white py-3 px-6 rounded-lg font-medium flex items-center gap-2 hover:bg-white/10 transition-colors">
                    <Globe className="h-5 w-5" />
                    <span>Publication Guidelines</span>
                  </button>
                </div>
              </div>
              <div className="hidden md:block">
                <motion.div 
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 5, repeat: Infinity }}
                >
                  <BookOpen className="h-24 w-24 opacity-80" />
                </motion.div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Download Form Modal */}
      {downloadFormVisible && selectedPublication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Download Publication</h3>
              <button 
                onClick={() => setDownloadFormVisible(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <h4 className="font-medium mb-2">{selectedPublication.title}</h4>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <FileText className="h-4 w-4" />
                <span>{selectedPublication.category.replace('-', ' ')}</span>
              </div>
            </div>

            {downloadSuccess ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <div className="bg-green-100 p-2 rounded-full">
                    <Check className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <h4 className="font-medium text-green-800 mb-2">Download Ready!</h4>
                <p className="text-green-700 text-sm">
                  Thank you for your interest. Your download is being prepared.
                </p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization/Company
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role/Position
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                  />
                </div>

                {downloadError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-red-700">{downloadError}</p>
                  </div>
                )}

                <div className="text-xs text-gray-500 mb-4">
                  By downloading this publication, you agree to receive occasional updates about AVN research and initiatives. We respect your privacy and will never share your information with third parties.
                </div>

                <button
                  type="submit"
                  disabled={downloading}
                  className="w-full bg-avn-purple-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 hover:bg-avn-purple-700 transition-colors disabled:opacity-70"
                >
                  {downloading ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Download className="h-5 w-5" />
                      <span>Download Publication</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PublicationsPage;